//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { FacebookEngagementMetricsAction } from '@facebook/actions/facebookEngagementMetricsActions';
import { FACEBOOK_ENGAGEMENT_ACTIONS } from '@facebook/constants/facebookConstants';
import {
  FacebookPageEngagementChartOptions as ChartSeriesOptions,
  FacebookPageEngagementSortDir as SortDir,
  FacebookPageEngagementSortKey as SortKey,
} from '@facebook/constants/facebookEngagementConstants';
import { IFacebookEngagementMetrics } from '@facebook/models/facebookStateModel';

const initialState: IFacebookEngagementMetrics = {
  recentPosts: [],
  totalPosts: 0,
  postSortKey: SortKey.PUBLISH_DATE,
  postSortDir: SortDir.DESC,
  recentPostsPagination: {
    currentPage: 0,
    itemsPerPage: 10,
  },
  engagementStats: {
    bubbleStats: undefined,
    comparisonBubbleStats: undefined,
    graphStats: [],
  },
  metricsBubbleStats: undefined,
  metricsGraphStats: [],
  engagementChartSeriesOption: ChartSeriesOptions.REACH,
  dateRange: {
    alias: 'last7Days',
    startDate: moment().subtract(6, 'days').toDate(),
    endDate: moment().toDate(),
  },
  errors: {
    fetchRecentPostsError: undefined,
    fetchEngagementStatsError: undefined,
  },
  flags: {
    isFetchingRecentPosts: false,
    isFetchingEngagementStats: false,
    isFetchingBubbleStats: false,
    isFetchingGraphStats: false,
  },
};

export default function FacebookEngagementMetricsReducer(
  state: IFacebookEngagementMetrics = initialState,
  action: FacebookEngagementMetricsAction,
): IFacebookEngagementMetrics {
  switch (action.type) {
    case FACEBOOK_ENGAGEMENT_ACTIONS.GET_RECENT_POSTS: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchRecentPostsError: undefined,
        },
        flags: {
          ...state.flags,
          isFetchingRecentPosts: true,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_RECENT_POSTS_SUCCEEDED: {
      return {
        ...state,
        recentPosts: action.recentPosts,
        totalPosts: action.totalPosts,
        flags: {
          ...state.flags,
          isFetchingRecentPosts: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_RECENT_POSTS_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchRecentPostsError: action.error,
        },
        flags: {
          ...state.flags,
          isFetchingRecentPosts: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.GET_ENGAGEMENT_STATS: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchEngagementStatsError: undefined,
        },
        flags: {
          ...state.flags,
          isFetchingEngagementStats: true,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_ENGAGEMENT_STATS_SUCCEEDED: {
      return {
        ...state,
        engagementStats: action.engagementStats,
        flags: {
          ...state.flags,
          isFetchingEngagementStats: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_ENGAGEMENT_STATS_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          fetchEngagementStatsError: action.error,
        },
        flags: {
          ...state.flags,
          isFetchingEngagementStats: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RESET_POST_PAGINATION: {
      return {
        ...state,
        recentPostsPagination: initialState.recentPostsPagination,
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.UPDATE_DATE_RANGE: {
      return {
        ...state,
        dateRange: action.dateRange,
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.UPDATE_CHART_SERIES_OPTION: {
      return {
        ...state,
        engagementChartSeriesOption: action.engagementChartSeriesOption,
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.UPDATE_POST_PAGINATION: {
      return {
        ...state,
        recentPostsPagination: action.pagination,
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.UPDATE_POST_SORT: {
      return {
        ...state,
        postSortKey: action.sortKey,
        postSortDir: action.sortDir,
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.REQUEST_BUBBLE_STATS: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingBubbleStats: true,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_BUBBLE_STATS_SUCCEDED: {
      return {
        ...state,
        metricsBubbleStats: action.bubbleStats,
        flags: {
          ...state.flags,
          isFetchingBubbleStats: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_BUBBLE_STATS_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingBubbleStats: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.REQUEST_GRAPH_STATS: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingGraphStats: true,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_GRAPH_STATS_SUCCEDED: {
      return {
        ...state,
        metricsGraphStats: action.graphStats,
        flags: {
          ...state.flags,
          isFetchingGraphStats: false,
        },
      };
    }
    case FACEBOOK_ENGAGEMENT_ACTIONS.RECEIVE_GRAPH_STATS_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingGraphStats: false,
        },
      };
    }
    default:
      return state;
  }
}
